import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronDown, BookOpen, Sparkles } from 'lucide-react';
import { useChapterNavigation } from '@/hooks/useChapterNavigation';
import { useReadingPreferences } from '@/hooks/useReadingPreferences';
import { decryptContent } from '@/components/reader/utils/contentDecryption';
import ChapterSkeleton from '@/components/ChapterSkeleton';
import ChapterNotFound from '@/components/ChapterNotFound';
import ChapterError from '@/components/ChapterError';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { getOptimizedImageUrl, createSrcSet, getImagePlaceholder } from '@/lib/imageOptimization';
import ChapterPreviewSEO from '@/components/SEO/ChapterPreviewSEO';

const ChapterPreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { chapter, isLoading, error } = useChapterNavigation(id);
  const { fontSize, fontFamily, alignment, theme } = useReadingPreferences();
  const [previewContent, setPreviewContent] = useState('');
  const [fullContent, setFullContent] = useState('');
  const [firstParagraph, setFirstParagraph] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const [showDebug, setShowDebug] = useState(false);
  const [searchParams] = useSearchParams();
  const [hasScrolled, setHasScrolled] = useState(false);
  const isMobile = useIsMobile();
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Get preview mode from URL parameter - either 'partial' (default) or 'full'
  const previewMode = searchParams.get('previewMode') || 'partial';
  const isFullPreview = previewMode === 'full';

  // Function to extract specific paragraphs
  const extractParagraphs = (html: string, start: number, end: number) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const paragraphs = doc.querySelectorAll('p');
    const selectedParagraphs = Array.from(paragraphs).slice(start, end);
    return selectedParagraphs.map(p => p.outerHTML).join('');
  };
  
  // Function to extract first paragraph for hero section
  const extractFirstParagraph = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const paragraphs = doc.querySelectorAll('p');
    if (paragraphs.length > 0) {
      const firstP = paragraphs[0];
      // Only get first 150 characters with ellipsis for hero display
      const text = firstP.textContent || '';
      const trimmedText = text.length > 150 ? text.substring(0, 150) + '...' : text;
      return trimmedText;
    }
    return '';
  };
  
  useEffect(() => {
    if (chapter?.content) {
      const loadContent = async () => {
        const decrypted = await decryptContent(chapter.content);
        setFullContent(decrypted);
        
        // Extract first paragraph for hero section
        setFirstParagraph(extractFirstParagraph(decrypted));
        
        // Get first paragraphs for preview
        const previewHtml = extractParagraphs(decrypted, 0, 10);
        setPreviewContent(previewHtml);
      };
      
      loadContent();
    }
  }, [chapter]);

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 50) {
        setHasScrolled(true);
      } else {
        setHasScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Enable body scroll for preview pages
  useEffect(() => {
    document.body.classList.add('preview-page-body');
    return () => {
      document.body.classList.remove('preview-page-body');
    };
  }, []);

  // Check URL for debugging flag
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('debug') === 'true') {
      console.log('ChapterPreview - Debug mode enabled');
      setShowDebug(true);
    }
  }, []);

  // Handle image load event
  const handleImageLoaded = () => {
    setImageLoaded(true);
  };

  // Navigate to signup with returnTo parameter
  const handleSignup = () => {
    if (!id) return;
    
    // Create the full path for redirecting back
    let returnPath = `/chapter/${id}`;
    
    // If in full preview mode and there's a next chapter, direct to the next chapter after signup
    if (isFullPreview && chapter?.next_chapter_id) {
      returnPath = `/chapter/${chapter.next_chapter_id}`;
      console.log('Full preview mode - will redirect to next chapter after signup:', returnPath);
    }
    
    const encodedReturnPath = encodeURIComponent(returnPath);
    console.log('Navigating to signup with returnTo:', returnPath);
    console.log('Encoded returnTo for signup:', encodedReturnPath);
    navigate(`/signup?returnTo=${encodedReturnPath}`);
  };

  // Navigate to login with returnTo parameter
  const handleLogin = () => {
    if (!id) return;
    
    // Create the full path for redirecting back
    let returnPath = `/chapter/${id}`;
    
    // If in full preview mode and there's a next chapter, direct to the next chapter after login
    if (isFullPreview && chapter?.next_chapter_id) {
      returnPath = `/chapter/${chapter.next_chapter_id}`;
      console.log('Full preview mode - will redirect to next chapter after login:', returnPath);
    }
    
    const encodedReturnPath = encodeURIComponent(returnPath);
    console.log('Navigating to login with returnTo:', returnPath);
    console.log('Encoded returnTo for login:', encodedReturnPath);
    navigate(`/login?returnTo=${encodedReturnPath}`);
  };

  // Toggle debug panel
  const toggleDebug = () => {
    setShowDebug(prev => !prev);
  };

  // Handle loading states
  if (isLoading) return <ChapterSkeleton />;
  if (error === 'Not Found') return <ChapterNotFound />;
  if (error) return <ChapterError error={error} />;

  // Get theme class and gradient colors based on theme
  const getThemeClasses = () => {
    switch (theme) {
      case 'dark':
        return 'bg-slate-900 text-gray-100';
      case 'sepia':
        return 'bg-amber-50 text-amber-900';
      case 'light':
      default:
        return 'bg-white text-gray-800';
    }
  };
  
  // Get gradient colors for text overlay
  const getTextOverlayGradient = () => {
    switch (theme) {
      case 'dark':
        return 'from-slate-900/90 to-slate-900/30';
      case 'sepia':
        return 'from-amber-50/90 to-amber-50/30';
      case 'light':
      default:
        return 'from-white/90 to-white/30';
    }
  };

  // Get overlay gradient based on theme
  const getOverlayGradient = () => {
    switch (theme) {
      case 'dark':
        return 'from-transparent via-slate-900/70 to-slate-900/90';
      case 'sepia':
        return 'from-transparent via-amber-50/70 to-amber-50/90';
      case 'light':
      default:
        return 'from-transparent via-white/70 to-white/90';
    }
  };

  // Get content gradient for fadeout based on theme
  const getContentFadeGradient = () => {
    switch (theme) {
      case 'dark':
        return 'from-transparent via-slate-900/80 to-slate-900';
      case 'sepia':
        return 'from-transparent via-amber-50/80 to-amber-50';
      case 'light':
      default:
        return 'from-transparent via-white/80 to-white';
    }
  };

  // Get accent color based on theme
  const getAccentColor = () => {
    switch (theme) {
      case 'dark':
        return 'text-primary-400';
      case 'sepia':
        return 'text-primary-800';
      case 'light':
      default:
        return 'text-primary';
    }
  };
  
  // Prepare content based on preview mode
  const visibleContent = isFullPreview 
    ? fullContent // Show entire chapter in full preview mode
    : extractParagraphs(fullContent, 0, 6); // Show first 6 paragraphs in partial preview
    
  const blurredContent = isFullPreview
    ? '' // No blurred content in full preview mode
    : extractParagraphs(fullContent, 6, 8); // Blur next 2 paragraphs in partial preview

  // Get the story cover image URL and details from the chapter data
  const storyCoverUrl = chapter?.stories?.cover_url || '/placeholder.svg';
  const optimizedCoverUrl = getOptimizedImageUrl(storyCoverUrl);
  const coverSrcSet = createSrcSet(storyCoverUrl);
  const imagePlaceholder = getImagePlaceholder(storyCoverUrl);
  const storyTitle = chapter?.stories?.title || 'História';
  const chapterTitle = chapter?.title || 'Capítulo';
  
  // Enhanced author name detection with detailed fallbacks
  // Log raw data first for debugging
  console.log("ChapterPreview - Author data check:", {
    storyData: chapter?.stories,
    authorData: chapter?.stories?.authors,
    rawAuthorProps: chapter?.stories?.authors
  });
  
  // Determine author name with fallbacks and logging
  let authorName = 'Autor';
  
  if (chapter?.stories?.authors) {
    const authors = chapter.stories.authors;
    if (authors.pen_name) {
      authorName = authors.pen_name;
      console.log("Using pen_name:", authorName);
    } else if (authors.full_name) {
      authorName = authors.full_name;
      console.log("Using full_name:", authorName);
    } else {
      // Check if authors might be an array (data structure issue)
      if (Array.isArray(authors) && authors.length > 0) {
        const firstAuthor = authors[0];
        authorName = firstAuthor.pen_name || firstAuthor.full_name || 'Autor';
        console.log("Using first author from array:", authorName);
      } else {
        console.warn("No valid author name found in:", authors);
      }
    }
  } else {
    console.warn("No authors data found in chapter:", chapter);
  }

  // Create meta description from first paragraph
  const metaDescription = firstParagraph || `Leia "${chapterTitle}" de "${storyTitle}" por ${authorName} no Pulse - a plataforma brasileira de histórias em texto e áudio.`;
  const pageTitle = `${chapterTitle} | ${storyTitle} por ${authorName} | Pulse`;

  return (
    <div className={`preview-container ${getThemeClasses()} min-h-screen pt-14 pb-24`}>
      {/* Premium Social Proof Banner */}
      <div className="fixed top-0 inset-x-0 z-50">
        <div className="relative">
          {/* Glow */}
          <div className="absolute inset-0 -top-6 -bottom-6 bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-rose-600/20 blur-2xl"></div>
          {/* Banner */}
          <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 text-white border-b border-white/20 shadow-lg">
            <div className="max-w-4xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-white/90" />
              <p className="text-sm md:text-base font-semibold tracking-tight">
                As autoras mais lidas do Brasil agora estão na Pulse: o primeiro app de romances 100% brasileiro
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic SEO Meta Tags */}
      <ChapterPreviewSEO

        title={pageTitle}
        description={metaDescription}
        chapterTitle={chapterTitle}
        storyTitle={storyTitle}
        authorName={authorName}
        coverImageUrl={optimizedCoverUrl}
        chapterId={id || ''}
      />

      {/* Hero Section - Full viewport height */}
      <div className="relative w-full h-[80vh] md:h-[90vh] overflow-hidden">
        {/* Optimized Image Loading - Replace CSS background with <img> */}
        <div className="absolute inset-0" style={{ backgroundColor: imagePlaceholder }}>
          <img 
            src={optimizedCoverUrl}
            srcSet={coverSrcSet}
            sizes="(max-width: 640px) 640px, (max-width: 960px) 960px, (max-width: 1200px) 1200px, 1920px"
            alt={`Capa de ${storyTitle} por ${authorName}`}
            className={cn(
              "w-full h-full object-cover object-center transition-opacity duration-500",
              imageLoaded ? "opacity-100" : "opacity-0"
            )}
            onLoad={handleImageLoaded}
            loading="eager"
            fetchPriority="high"
            width="1200"
            height="800"
            style={{
              transform: hasScrolled ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out'
            }}
          />
          
          {/* Placeholder while image loads */}
          {!imageLoaded && (
            <div className="absolute inset-0 animate-pulse"></div>
          )}
        </div>
        
        {/* Dark gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent"></div>
        
        {/* Content overlay with frosted glass effect */}
        <div className="absolute inset-x-0 bottom-0 px-4 py-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-4 animate-fade-in">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-1">{storyTitle}</h1>
              <p className="text-lg text-white/80">por {authorName}</p>
            </div>
            
            {/* Reading time indicator */}
            <div className="flex items-center gap-3 mb-6 animate-fade-in" style={{animationDelay: '0.1s'}}>
              <div className="text-sm text-white/70 flex items-center gap-1">
                <BookOpen className="h-4 w-4" />
                <span>5 min de leitura</span>
              </div>
            </div>
            
            {/* First paragraph preview */}
            <div className="max-w-2xl animate-fade-in" style={{animationDelay: '0.2s'}}>
              <p className="text-lg text-white/90 leading-relaxed">
                {firstParagraph}
              </p>
            </div>
            
            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center animate-fade-in" style={{animationDelay: '0.3s'}}>
              <span className="text-white/70 text-sm mb-2">Continue lendo</span>
              <ChevronDown className="animate-bounce h-6 w-6 text-white" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Content Section */}
      <div className={`relative ${getThemeClasses()} pt-10 pb-24`}>
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          {/* Visible content */}
          <div 
            className="prose prose-lg max-w-none space-y-6"
            style={{
              fontSize: '20px',
              fontFamily,
              textAlign: alignment
            }}
            dangerouslySetInnerHTML={{ __html: visibleContent }}
          />
          
          {/* Blurred content container - only for partial preview mode */}
          {!isFullPreview && blurredContent && (
            <div className="relative">
              {/* The content to be blurred */}
              <div
                className="prose prose-lg max-w-none space-y-6 pt-0 pb-24"
                style={{
                  fontSize: '20px',
                  fontFamily,
                  textAlign: alignment
                }}
                dangerouslySetInnerHTML={{ __html: blurredContent }}
              />
              
              {/* Gradient overlay for blurred content */}
              <div className={`absolute inset-0 bg-gradient-to-b ${getContentFadeGradient()} backdrop-blur-[2px]`}></div>
              
              {/* Continue reading indicator */}
              <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 text-center pointer-events-none z-30">
                <div className={`flex flex-col items-center ${getAccentColor()}`}>
                  <span className="font-medium text-sm mb-2">A história continua</span>
                  <ChevronDown className="animate-bounce h-5 w-5" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Enhanced Login/Signup Wall */}
      <div className={`${getThemeClasses()} border-t border-primary/10 py-10`}>
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-primary-100/50 to-primary-100/10 backdrop-blur-sm dark:from-primary-900/20 dark:to-primary-900/5">
            <CardContent className="p-8">
              <div className="flex flex-col items-center space-y-6 text-center">
                {/* Compelling headline - different for each preview mode */}
                <h3 className="text-2xl font-bold">
                  {isFullPreview 
                    ? `Gostou deste capítulo? ${chapter?.next_chapter_id ? 'Cadastre-se para ler o próximo!' : 'Cadastre-se para mais histórias!'}` 
                    : "A história está apenas começando..."}
                </h3>
                
                {/* Value proposition */}
                <p className="text-base max-w-lg">
                  {isFullPreview
                    ? "Acesse o Pulse para continuar sua jornada com histórias emocionantes e capítulos em áudio."
                    : "Crie sua conta gratuita e mergulhe nessa história — com capítulos em áudio, e muitas outras histórias tão emocionantes quanto."}
                </p>
                
                {/* Feature list */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-left my-2 w-full max-w-md">
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-primary/5">
                    <div className="rounded-full bg-primary/10 p-2">
                      <span className="block">🎧</span>
                    </div>
                    <span>Todos os capítulos em áudio</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-primary/5">
                    <div className="rounded-full bg-primary/10 p-2">
                      <span className="block">📖</span>
                    </div>
                    <span>Histórias exclusivas</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-primary/5">
                    <div className="rounded-full bg-primary/10 p-2">
                      <span className="block">🧠</span>
                    </div>
                    <span>Chat com personagens</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-primary/5">
                    <div className="rounded-full bg-primary/10 p-2">
                      <span className="block">📱</span>
                    </div>
                    <span>Disponível em todos dispositivos</span>
                  </div>
                </div>
                
                {/* CTA movido para barra fixa inferior */}

                
                {/* Brand message */}
                <p className="text-sm font-medium text-primary italic mt-4">
                  Pulse — a plataforma brasileira de histórias do jeitinho que você gosta
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Fixed Bottom CTA - Premium */}
      <div className="fixed bottom-0 inset-x-0 z-50">
        <div className="relative">
          <div className="absolute inset-0 -top-6 -bottom-6 bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-rose-600/20 blur-2xl"></div>
          <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 text-white border-t border-white/20 shadow-lg">
            <div className="max-w-3xl mx-auto p-3">
              <a
                href={(typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent)) ? 'https://apps.apple.com/br/app/pulse-romance/id6746346109' : 'https://play.google.com/store/apps/details?id=app.pulsefiction.pulse'}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button size="lg" className="w-full h-12 text-base font-semibold bg-white text-purple-700 hover:bg-white/90 rounded-full shadow-xl">
                  continue lendo gratuitamente
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>


      {/* Debug Panel - only visible when ?debug=true in URL */}
      {showDebug && (
        <div className="p-4 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg max-w-4xl mx-auto my-8 text-xs">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold">Auth Flow Debug Panel</h3>
              <button 
                onClick={toggleDebug}
                className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded"
              >
                Close
              </button>
            </div>
            
            <div className="space-y-2">
              <div>
                <p><strong>Chapter ID:</strong> {id}</p>
                <p><strong>Preview Mode:</strong> {previewMode}</p>
                <p><strong>Next Chapter ID:</strong> {chapter?.next_chapter_id || 'N/A'}</p>
                <p><strong>Return Path:</strong> {isFullPreview && chapter?.next_chapter_id 
                  ? `/chapter/${chapter.next_chapter_id}` 
                  : `/chapter/${id}`}
                </p>
                <p><strong>Encoded Return:</strong> {encodeURIComponent(isFullPreview && chapter?.next_chapter_id 
                  ? `/chapter/${chapter.next_chapter_id}` 
                  : `/chapter/${id}`)}</p>
                <p><strong>Auth Status:</strong> {user ? 'Logged In' : 'Not Logged In'}</p>
                {user && (
                  <p><strong>User Email:</strong> {user.email}</p>
                )}
                <p><strong>Author Data:</strong> {JSON.stringify({
                  authorName,
                  rawAuthorData: chapter?.stories?.authors,
                  penName: chapter?.stories?.authors?.pen_name,
                  fullName: chapter?.stories?.authors?.full_name
                })}</p>
              </div>
              
              <div className="pt-2 border-t border-gray-300 dark:border-gray-700">
                <p className="font-semibold mb-1">Preview Mode Test Links:</p>
                <div className="flex space-x-2">
                  <a 
                    href={`/preview/chapter/${id}`}
                    className="px-2 py-1 bg-blue-500 text-white rounded text-xs"
                  >
                    Partial Preview
                  </a>
                  <a 
                    href={`/preview/chapter/${id}?previewMode=full`} 
                    className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                  >
                    Full Preview
                  </a>
                  <a 
                    href={`/preview/chapter/${id}?previewMode=full&debug=true`} 
                    className="px-2 py-1 bg-purple-500 text-white rounded text-xs"
                  >
                    Debug Full Preview
                  </a>
                </div>
              </div>
              
              <div className="pt-2 border-t border-gray-300 dark:border-gray-700 mt-2">
                <p className="font-semibold mb-1">Debug URLs:</p>
                <p><strong>Signup URL:</strong> {`/signup?returnTo=${encodeURIComponent(`/chapter/${id}`)}`}</p>
                <p><strong>Login URL:</strong> {`/login?returnTo=${encodeURIComponent(`/chapter/${id}`)}`}</p>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default ChapterPreview;
